import React, { useMemo } from 'react';
import { FileText, Plus, ShieldCheck, Trash2 } from 'lucide-react';
import { Button } from '../../../shared/components/Button';
import { EmptyState } from '../../../shared/components/EmptyState';
import { SkeletonText } from '../../../shared/components/Skeleton';
import {
  useAdicionarClausula,
  useClausulasContrato,
  useRemoverClausula,
  useTemplatesClausulas,
} from '../hooks';

interface Props {
  contractId: string;
  modality?: string | null;
  canEdit: boolean;
}

/**
 * Cláusulas aplicadas ao contrato + catálogo de templates disponíveis
 * por modalidade (RN-COM-029). O bloqueio de conclusão por cláusula
 * obrigatória ausente já existe no backend
 * (`ContractsService.checkMandatoryClauses`) — esta seção é só onde o
 * usuário resolve a pendência.
 */
export function ContractClausesSection({ contractId, modality, canEdit }: Props) {
  const { data: clauses = [], isLoading } = useClausulasContrato(contractId);
  const { data: templates = [] } = useTemplatesClausulas(contractId, modality ?? undefined);
  const { mutate: addClause, isPending: isAdding } = useAdicionarClausula();
  const { mutate: removeClause } = useRemoverClausula();

  const appliedSlugs = useMemo(() => new Set(clauses.map((c) => c.slug)), [clauses]);
  const availableTemplates = templates.filter((t) => t.isActive && !appliedSlugs.has(t.slug));

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h2 className="text-base font-semibold text-gray-900 mb-4">Cláusulas</h2>

      {isLoading ? (
        <SkeletonText lines={3} />
      ) : clauses.length === 0 ? (
        <EmptyState size="compact" icon={<FileText className="h-8 w-8" />} title="Nenhuma cláusula aplicada ainda" />
      ) : (
        <ul className="divide-y divide-gray-100 text-sm mb-4">
          {clauses.map((c) => (
            <li key={c.id} className="py-2 flex items-start justify-between gap-3">
              <div>
                <p className="font-medium text-gray-800 flex items-center gap-1.5">
                  {c.title}
                  {c.isMandatory && (
                    <span title="Obrigatória">
                      <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                    </span>
                  )}
                </p>
                <p className="text-xs text-gray-500 line-clamp-2">{c.content}</p>
              </div>
              {canEdit && (
                <button
                  onClick={() => removeClause({ id: contractId, clauseId: c.id })}
                  className="text-gray-400 hover:text-red-600 shrink-0"
                  title="Remover cláusula"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit && availableTemplates.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Disponíveis no catálogo{modality ? ` (${modality})` : ''}</p>
          <ul className="space-y-1.5">
            {availableTemplates.map((t) => (
              <li key={t.id} className="flex items-center justify-between gap-3 text-sm">
                <span className="text-gray-700 flex items-center gap-1.5">
                  {t.name}
                  {t.isMandatory && (
                    <span title="Obrigatória">
                      <ShieldCheck className="w-3.5 h-3.5 text-amber-600" />
                    </span>
                  )}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isAdding}
                  icon={<Plus className="w-3.5 h-3.5" />}
                  onClick={() =>
                    addClause({
                      id: contractId,
                      payload: {
                        templateId: t.id,
                        slug: t.slug,
                        title: t.name,
                        content: t.template ?? t.description ?? '',
                        isMandatory: t.isMandatory,
                      },
                    })
                  }
                >
                  Adicionar
                </Button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
